import { Header, HeaderLogo, HeaderSideNav, LanguageToggle, UserProfile } from '@tameeni/header';
import { Footer } from '@tameeni/footer';
import useInitLanguage from '@Hooks/useInitLanguage';
import { useAuth } from 'react-oidc-context';
import useModal from '@Hooks/useModal';
import LoginPopup from '@Components/common/LoginPopup';
import { useTranslation } from 'react-i18next';
import { ReactNode, useState } from 'react';
import { dataLayerPush } from '@Services/ga4';
import { GA4Events } from '@Services/ga4Events';
import TameeniLoader from '@Components/common/TameeniLoader';
import { useLocation, useNavigate } from 'react-router-dom';
import { Products } from '@tameeni/shared-logic';
import { useHomeLinks } from './hooks/useHomeLinks';
import AdsBanner from './AdsBanner';

const PAGES_WITHOUT_LOGIN = [
  '/mmp',
  '/en/mmp',
  '/travel',
  '/en/travel',
  '/health-sme',
  '/en/health-sme',
  '/health',
  '/en/health',
  '/ps',
  '/en/ps',
  '/fleet',
  '/en/fleet',
  '/home-insurance',
  '/en/home-insurance',
  '/premium-residency',
  '/en/premium-residency',
];

export default function Layout({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation(['translation', 'routes', 'home']);
  const locale = i18n.language as 'ar' | 'en';
  const { switchLanguage, isLoading: isSwitchingLanguage } = useInitLanguage();
  const auth = useAuth();
  const { showModal, handleCloseModal, handleShowModal, modalTarget } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    HOME_LINKS,
    HEADER_SUPPORT_LINKS,
    HEADER_RESOURCES_LINKS,
    FOOTER_RESOURCES_LINKS,
    FOOTER_SUPPORT_LINKS,
    PRODUCTS_FLAT,
    STORE_LINKS,
    PRODUCTS,
    SOCIAL_LINKS,
    getActiveProduct,
  } = useHomeLinks();

  // Handlers
  const handleLogin = () => {
    document.getElementById('Login-popup-trigger')?.click();
  };

  const handleLogout = () => {
    if (auth.isAuthenticated) {
      setIsLoading(true);
      dataLayerPush(GA4Events.logout.event, []);
      auth.signoutSilent().then(() => {
        navigate('/', { replace: true });
        localStorage.clear();
        setIsLoading(false);
      });
    }
  };

  // Data
  const loginMenuItems = [
    {
      label: t('translation:myAccount'),
      link: '/individual/profilepage/dashboard',
      icon: 'Settings',
    },
  ];

  if (isLoading || isSwitchingLanguage) return <TameeniLoader />;

  return (
    <>
      {!PAGES_WITHOUT_LOGIN.includes(pathname) && <AdsBanner />}
      <Header>
        <div className='box h-16 flex items-center gap-4 z-50'>
          <HeaderSideNav
            androidAppUrl={STORE_LINKS.googlePlay}
            appGalleryUrl={STORE_LINKS.appGallery}
            iosAppUrl={STORE_LINKS.appStore}
            direction={locale === 'ar' ? 'right' : 'left'}
            downloadAppLabel={t('home:get_app_title')}
            followUsLabel={t('home:follow_us')}
            logo={locale === 'ar' ? 'TameeniLogoAR' : 'TameeniLogoEN'}
            logoClassName='w-[165px]'
            sideNav={
              [
                {
                  items: PRODUCTS,
                  label: t('translation:products-label'),
                },
                HEADER_SUPPORT_LINKS[getActiveProduct()]
                  ? {
                      items: HEADER_SUPPORT_LINKS[getActiveProduct()],
                      label: t('translation:support-label'),
                    }
                  : undefined,
                HEADER_RESOURCES_LINKS[getActiveProduct()]
                  ? {
                      items: HEADER_RESOURCES_LINKS[getActiveProduct()],
                      label: t('translation:resources-label'),
                    }
                  : undefined,
              ].filter((o) => o !== undefined) as {
                items: { label: string; link: string; icon: string }[];
                label: string;
              }[]
            }
            socialLinks={SOCIAL_LINKS}
          />
          <div className='grow ms-0 md:ms-4'>
            <HeaderLogo
              label={t(
                `products:${Object.keys(Products).find(
                  (key) => Products[key as keyof typeof Products] === getActiveProduct(),
                )}`,
              )}
              link='/'
              logoClassName='w-[165px]'
              mainLogo={locale === 'ar' ? 'TameeniLogoAR' : 'TameeniLogoEN'}
            />
          </div>
          <LanguageToggle
            label={locale === 'en' ? 'العربية' : 'English'}
            onLanguageChange={switchLanguage}
          />
          <UserProfile
            isLoggedIn={auth.isAuthenticated}
            loginLabel={t('translation:login')}
            logoutLabel={t('translation:logout')}
            onLogin={handleLogin}
            onLogout={handleLogout}
            profileMenuItems={loginMenuItems}
            user={{
              name: auth.user?.profile?.email as string,
              id: auth.user?.profile?.IdentityNumber as string,
            }}
          />
        </div>
      </Header>
      <main>{children}</main>

      <Footer
        locale={locale}
        rootHref={`/${locale === 'en' ? 'en' : ''}`}
        productsToShow={PRODUCTS_FLAT}
        links={HOME_LINKS}
        resourcesLinks={FOOTER_RESOURCES_LINKS[getActiveProduct()]}
        supportLinks={FOOTER_SUPPORT_LINKS[getActiveProduct()]}
      />

      <a
        id='Login-popup-trigger'
        className='hidden'
        href='#Login-popup'
        data-target-modal='#Login-popup'
        data-testid='login-popup'
        onClick={(e: React.MouseEvent<HTMLElement>) => handleShowModal(e)}
      />
      {showModal && modalTarget === '#Login-popup' && <LoginPopup handleClose={handleCloseModal} />}
    </>
  );
}
